import { useCallback, useMemo } from 'react';
import { useImageStore } from '@/entities/image/store';

const DISPLAY_FIELDS: Array<{ key: string; label: string; category: string }> = [
  { key: 'Title', label: 'Title', category: 'descriptive' },
  { key: 'Description', label: 'Description', category: 'descriptive' },
  { key: 'Keywords', label: 'Keywords', category: 'descriptive' },
  { key: 'ImageWidth', label: 'Width', category: 'technical' },
  { key: 'ImageHeight', label: 'Height', category: 'technical' },
  { key: 'FileType', label: 'File Type', category: 'technical' },
  { key: 'MIMEType', label: 'MIME Type', category: 'technical' },
  { key: 'Make', label: 'Camera Make', category: 'technical' },
  { key: 'Model', label: 'Camera Model', category: 'technical' },
  { key: 'DateTimeOriginal', label: 'Date Taken', category: 'technical' },
  { key: 'FNumber', label: 'F-Number', category: 'technical' },
  { key: 'ExposureTime', label: 'Exposure', category: 'technical' },
  { key: 'ISO', label: 'ISO', category: 'technical' },
  { key: 'FocalLength', label: 'Focal Length', category: 'technical' },
  { key: 'GPSLatitude', label: 'GPS Latitude', category: 'gps' },
  { key: 'GPSLongitude', label: 'GPS Longitude', category: 'gps' },
  { key: 'GPSPosition', label: 'GPS Position', category: 'gps' },
];

export default function ExifSidebar() {
  const activeImagePath = useImageStore((s) => s.activeImagePath);
  const images = useImageStore((s) => s.images);
  const searchQuery = useImageStore((s) => s.searchQuery);
  const imageExif = useImageStore((s) => s.imageExif);
  const stageChange = useImageStore((s) => s.stageChange);
  const stagedChanges = useImageStore((s) => s.stagedChanges);
  const updateableFields = useImageStore((s) => s.updateableFields);
  const toggleFieldUpdate = useImageStore((s) => s.toggleFieldUpdate);
  const generateBulkLLMExif = useImageStore((s) => s.generateBulkLLMExif);
  const addNotification = useImageStore((s) => s.addNotification);
  const selectedIndices = useImageStore((s) => s.selectedIndices);
  const llmLoadingPaths = useImageStore((s) => s.llmLoadingPaths);
  const selectedCount = selectedIndices.size;
  
  const filteredImages = useMemo(() => {
    if (!searchQuery) return images;
    const q = searchQuery.toLowerCase();
    return images.filter(img => img.name.toLowerCase().includes(q));
  }, [images, searchQuery]);

  const activeExif = activeImagePath ? imageExif.get(activeImagePath) || {} : {};
  const imageStaged = activeImagePath ? stagedChanges.get(activeImagePath) : undefined;

  const handleGenerateAI = useCallback(async () => {
    let pathsToProcess: string[] = [];
    if (selectedCount > 0) {
      pathsToProcess = [...selectedIndices].map(idx => filteredImages[idx]?.path).filter(Boolean);
    } else if (activeImagePath) {
      pathsToProcess = [activeImagePath];
    }

    if (pathsToProcess.length === 0) {
      addNotification('warning', 'No images selected to process.');
      return;
    }

    await generateBulkLLMExif(pathsToProcess);
  }, [selectedCount, selectedIndices, filteredImages, activeImagePath, generateBulkLLMExif, addNotification]);

  const grouped: Record<string, typeof DISPLAY_FIELDS> = {};
  for (const field of DISPLAY_FIELDS) {
    if (!grouped[field.category]) grouped[field.category] = [];
    grouped[field.category].push(field);
  }

  return (
    <aside className="w-full flex flex-col bg-gray-800 border-l border-gray-700 h-full">
      <div className="flex items-center justify-between px-3 py-2 border-b border-gray-700">
        <h2 className="text-sm font-semibold">EXIF Metadata</h2>
        <button
          onClick={handleGenerateAI}
           disabled={llmLoadingPaths.size > 0}
          className="px-2 py-1 text-xs bg-purple-600 hover:bg-purple-700 disabled:bg-gray-600 disabled:text-gray-400 rounded transition"
        >
           {(llmLoadingPaths.size > 0) ? 'Generating...' : (selectedCount > 1 ? `Generate EXIF for ${selectedCount} images` : 'Generate with AI')}
        </button>
      </div>
      
      {!activeImagePath ? (
        <div className="flex-1 flex items-center justify-center text-gray-500 text-sm">
          Select an image to view EXIF data
        </div>
      ) : (
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {Object.entries(grouped).map(([category, fields]) => (
            <div key={category}>
              <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-2">{category}</h3>
              <div className="space-y-1">
                {fields.map((f) => {
                  const staged = imageStaged?.get(f.key);
                  const effectiveValue = staged?.newValue ?? (activeExif as any)?.[f.key] ?? '';
                  const displayValue = effectiveValue === undefined || effectiveValue === null ? '' : String(effectiveValue);
                  const hasDiff = !!staged;
                  
                  return (
                    <div key={f.key} className={`flex flex-col gap-0.5 py-1 border-b border-gray-700/50 ${hasDiff ? 'bg-yellow-900/20' : ''}`}>
                      <div className="flex items-center justify-between">
                        <label className="text-xs text-gray-400 font-medium">{f.label}</label>
                        <input 
                          type="checkbox"
                          checked={!!updateableFields[f.key]}
                          onChange={(e) => toggleFieldUpdate(f.key, e.target.checked)}
                          className="w-3 h-3 rounded text-purple-600 bg-gray-700 border-gray-600 focus:ring-purple-500 focus:ring-1"
                        />
                      </div>
                      <input
                        type="text"
                        value={selectedCount > 1 ? '' : displayValue}
                        disabled={selectedCount > 1}
                        onChange={(e) => {
                          if (selectedCount <= 1) {
                            const originalVal = (activeExif as any)?.[f.key] ?? null;
                            const newVal = e.target.value;
                            stageChange(activeImagePath!, f.key, newVal, originalVal);
                          }
                        }}
                        placeholder={selectedCount > 1 ? `Batch update enabled` : ''}
                        className={`text-sm text-gray-200 bg-gray-700 border border-gray-600 rounded-md px-2 py-1 w-full ${hasDiff ? 'border-yellow-500' : ''} ${selectedCount > 1 ? 'opacity-50 cursor-not-allowed' : ''} focus:outline-none focus:ring-1 focus:ring-blue-500`}
                      />
                       {hasDiff && <div className="text-xs text-yellow-400 mt-0.5 truncate">was: {staged.oldValue == null ? '(empty)' : String(staged.oldValue)}</div>}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </aside>
  );
}